import * as Sentry from '@sentry/react';
import { useMutationState } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getClosedDateDescription, TDR, toLuxon } from 'tdr-common';
import { useSupportedGroupSizes } from '../../api/availability/availability';
import { useSearch } from '../../api/availability/search';
import { MUTATION_KEY, useDeletePendingMods } from '../../api/deletePendingModifications';
import { useReservation } from '../../api/getReservation';
import { useRestaurant } from '../../api/getRestaurant';
import { useRestaurantBasePolicies } from '../../api/getRestaurantBasePolicies';
import { useTables } from '../../api/getRestaurantTables';
import { useRestaurantTaxPolicies } from '../../api/getRestaurantTaxPolicies';
import { useTable } from '../../api/getTable';
import { useModifyReservation } from '../../api/modifyReservation';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import { URLParams } from '../../common/types';
import { SameDayBooking } from '../../components/__design-system/modal/exit/same-day-booking';
import { UnavailableDate } from '../../components/__design-system/modal/exit/unavailable-date';
import { BookingDetailsSummary } from '../../components/booking-details-summary/BookingDetailsSummary';
import { CTAButton } from '../../components/buttons';
import { SquareIconButton } from '../../components/buttons/SquareIconButton';
import { isToday, toLocalJsDate } from '../../components/calendar/Calendar.utils';
import { Header } from '../../components/checkout/Header';
import { DiscardChanges } from '../../components/dialog/controlled-dialog/DiscardChanges';
import { ModificationNotice } from '../../components/notice/ModificationNotice';
import {
	PartySizeOption
} from '../../components/shared/PartySizeDropdown';
import { buildGroupSizeOptions } from '../../components/shared/PartySizeSelector';
import { Skeleton } from '../../components/skeleton/Skeleton';
import { TimeSelector } from '../../components/shared/TimeSelector';
import { useModificationFlowState } from '../../context/ModificationFlowProvider';
import { getNewInvoiceForModifiedEpochOrGuestCount } from '../../helpers/getNewInvoiceForModifiedEpoch';
import luxonToFirebaseTimestamp from '../../helpers/luxonToFirebaseTimestamp';
import { trackReservationModifications } from '../../helpers/reservationEventTrackers';
import { timeStringToEpoch } from '../../helpers/timeStringToEpoch';
import { ModifyBookingFormData } from '../../routes/BookingGuard';
import { PartySizePicker } from '@/components/shared/PartySizePicker';
import { AvailabilityDatePicker } from '@/components/shared/AvailabilityDatePicker';
import { useTranslation } from 'react-i18next';

const ModifcationSearchTables = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { reservationId } = useParams<URLParams>();

	const { newInvoice, setNewInvoice } = useModificationFlowState();

	const { data: reservation, refetch: refetchReservation } = useReservation(reservationId);
	const { data: restaurant } = useRestaurant(reservation.restaurantId);
	const { data: tables } = useTables(reservation.restaurantId);
	const { data: selectedTable } = useTable(reservation.tableId);
	const { data: restaurantPolicies } = useRestaurantBasePolicies(restaurant.id);
	const { data: taxPolicies } = useRestaurantTaxPolicies(restaurant);
	const { supportedGroupSizes, isLoading: isLoadingGroupSizes } = useSupportedGroupSizes({ tables });

	const modifyReservationMutation = useModifyReservation(reservationId);
	const deletePendingModsMutation = useDeletePendingMods(reservationId);

	const deletePendingMutationState = useMutationState({
		filters: { mutationKey: [MUTATION_KEY] },
		select: (mutation) => mutation.state.status
	})[0]; // useMutationState always returns an array

	const methods = useFormContext<ModifyBookingFormData>();

	const [tableIds, setTableIds] = useState<string[]>();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [sameDayBookingOpen, setSameDayBookingOpen] = useState(false);
	const [noAvailabilityOpen, setNoAvailabilityOpen] = useState(false);
	// const [pastHorizonOpen, setPastHorizonOpen] = useState(false);
	const [lastUnavailableDateClicked, setLastUnavailableDateClicked] = useState<DateTime>(null);

	const [groupSizeOptions] = buildGroupSizeOptions(supportedGroupSizes, null, false, t);

	const watchEpochInSeconds = methods.watch('epochInSeconds');
	const watchGroupSize = methods.watch('guests');

	const [selectedDate, setSelectedDate] = useState<string>(
		toLuxon(watchEpochInSeconds, restaurant.timezone).toFormat('yyyy-MM-dd')
	);
	const [selectedTime, setSelectedTime] = useState<string>(
		toLuxon(watchEpochInSeconds, restaurant.timezone).toFormat('HH:mm')
	);

	const [searchFilters, setSearchFilters] = useState({
		groupSize: methods.watch('guests'),
		date: selectedDate,
		time: selectedTime
	});

	// const { data: entityWithPolicies } = useEntityWithAvailabilityRestrictingPolicies({
	// 	slug: restaurant.slug,
	// 	id: selectedTable?.id,
	// 	searchParams: searchFilters
	// });

	const { results, isLoading: isSearching } = useSearch({
		params: searchFilters,
		restaurantId: restaurant.id,
		space: tables,
		options: {
			releaseReservationId: reservationId
		}
	});

	const resetFormAndExit = () => {
		methods.reset();
		setNewInvoice(undefined);
		navigate(`/booking/${reservationId}`);
	};

	const handleExitClick = () => {
		if (methods.formState.isDirty) {
			setIsConfirmOpen(true);
		}
		else {
			resetFormAndExit();
		}
	};

	const handleBackClick = handleExitClick;

	const handleConfirmExit = () => {
		resetFormAndExit();
		setIsConfirmOpen(false);
	};

	const handleCancelExit = () => {
		setIsConfirmOpen(false);
	};

	const handleGroupSizeChange = (option: PartySizeOption) => {
		methods.setValue('guests', Number(option.value), { shouldDirty: true });
	};

	const handleSeatingChangeClick = () => {
		navigate(`/booking/${reservationId}/modify/seating-options`);
	};

	const handleReviewClick = async () => {
		// Payment required
		if (newInvoice.payable > 0) {
			const response = await modifyReservationMutation.mutateAsync({
				id: reservationId,
				price: newInvoice.total,
				guests: methods.getValues('guests'),
				time: luxonToFirebaseTimestamp(DateTime.fromSeconds(watchEpochInSeconds))
			});

			if (response.success) {
				methods.setValue('stripeClientSecret', response.clientSecret);
				methods.setValue('stripePaymentMethod', response.paymentMethod);
				navigate(`/booking/${reservationId}/payment`);
			}
			else {
				toast.error(response.message || t('errors.oops_something_went_wrong'));
				Sentry.captureMessage(`modifyReservation Error: ${response.message}`);
			}
		}
		// Refund required
		else {
			navigate(`/booking/${reservationId}/refund`);
		}
	};

	const handleConfirmChangesClick = async () => {
		const updatedTimestamp = luxonToFirebaseTimestamp(DateTime.fromSeconds(watchEpochInSeconds));
		const response = await modifyReservationMutation.mutateAsync({
			id: reservationId,
			price: newInvoice.total,
			time: updatedTimestamp,
			guests: methods.getValues('guests')
		});
		if (response.success) {
			navigate(`/booking/${reservationId}`);
			trackReservationModifications({ reservation, itemsChanged: { time: updatedTimestamp, price: newInvoice.total } });
			toast.success(t('messages.changes_saved', { defaultValue: 'Changes saved successfully!' }));
		}
		else {
			const errorMessage = `modifyReservation Error: ${response.message}`;
			const sentryErrorID = Sentry.captureException(new Error(errorMessage));
			toast.error(response.message || t('errors.oops_something_went_wrong'));
			trackReservationModifications({ reservation, errorMessage, sentryErrorID });
		}
	};

	const handleDateChange = async (date: DateTime, availabilityPercent: number) => {
		const isSameDay = isToday(toLocalJsDate(date)) && availabilityPercent === 0;

		if (isSameDay) {
			setSameDayBookingOpen(true);
		}
		else if (availabilityPercent > 0) {
			setSameDayBookingOpen(false);
			setNoAvailabilityOpen(false);
			setLastUnavailableDateClicked(null);
		}
		else {
			setNoAvailabilityOpen(true);
			setLastUnavailableDateClicked(date);
		}

		setSelectedDate(date.toFormat('yyyy-MM-dd'));
	};

	const handleTimeslotChange = async (newTime: string | null) => {
		if (!newTime) {
			return; // Exit early if no time is selected
		}
		const newEpochInSeconds = timeStringToEpoch(methods.getValues('epochInSeconds'), newTime, reservation.timezone);
		setSelectedTime(toLuxon(newEpochInSeconds, restaurant.timezone).toFormat('HH:mm'));
	};

	useEffect(() => {
		if (selectedDate && selectedTime) {
			const newEpochInSeconds = DateTime.fromFormat(`${selectedDate}__${selectedTime}`, 'yyyy-MM-dd__HH:mm', {
				zone: restaurant.timezone
			}).toSeconds();
			methods.setValue('epochInSeconds', newEpochInSeconds, { shouldDirty: true });
		}
		else if (selectedDate) {
			const newEpochInSeconds = DateTime.fromFormat(selectedDate, 'yyyy-MM-dd', {
				zone: restaurant.timezone
			}).toSeconds();
			methods.setValue('epochInSeconds', newEpochInSeconds, { shouldDirty: true });
		}
	}, [selectedDate, selectedTime]);

	useEffect(() => {
		if (results?.metadata?.date && !isLoading) {
			setTableIds(results.metadata.date);
		}
	}, [results?.metadata?.date]);

	useEffect(() => {
		setSearchFilters({
			groupSize: methods.watch('guests'),
			date: selectedDate,
			time: selectedTime
		});
	}, [selectedDate, selectedTime, methods]);

	useEffect(() => {
		if (noAvailabilityOpen) {
			setSelectedTime(null);
		}
	}, [noAvailabilityOpen]);

	useEffect(() => {
		if (!results || isSearching) {
			return;
		}

		if (selectedTime && results.metadata.time.length === 0) {
			setSelectedTime(null);
		}
	}, [results, isSearching, selectedTime, setSelectedTime]);

	useEffect(() => {
		if (reservation.status === TDR.Reservation.Status.Pending) {
			// Delete any pending changes and refetch reservation (in case user dropped off (or used browser back button) with pending changes while previously modifying their booking)
			if (reservation['expirationTask'] && reservation.pendingChanges) {
				deletePendingModsMutation.mutateAsync(reservationId).then(() => {
					refetchReservation();
					setNewInvoice(undefined);
				});
			}
		}
	}, [reservation, refetchReservation]);

	useEffect(() => {
		if (deletePendingMutationState === 'pending' || reservation.status === TDR.Reservation.Status.Pending) {
			return;
		}

		const calculateNewInvoice = async (newEpochInSeconds: number) => {
			if (newEpochInSeconds !== reservation.time.seconds || watchGroupSize !== reservation.guests) {
				const newInvoice = await getNewInvoiceForModifiedEpochOrGuestCount({
					restaurant,
					table: selectedTable,
					newEpochInSeconds,
					newGuestCount: watchGroupSize,
					existingReservation: reservation,
					basePolicies: [...(restaurantPolicies ?? []), ...(taxPolicies ?? [])]
				});
				setNewInvoice(newInvoice);
			}
		};

		calculateNewInvoice(watchEpochInSeconds);
	}, [watchEpochInSeconds, watchGroupSize, deletePendingMutationState, reservation.status]);

	const isLoading = isSearching || isLoadingGroupSizes;
	const areFiltersComplete = !!searchFilters.groupSize && !!searchFilters.date && !!searchFilters.time;
	const currentTableSupportsNewParams =
		methods.formState.isDirty && !isLoading ? results?.metadata.time.includes(selectedTable.id) : true;
	const isPaymentStepRequired = newInvoice && newInvoice.payable !== 0;

	const ctaText = currentTableSupportsNewParams
		? isPaymentStepRequired
			? t('actions.review_change', { defaultValue: 'Review Change' })
			: t('checkout.cta_labels.modify', { defaultValue: 'Confirm Change' })
		: t('actions.view_seating_options', { defaultValue: 'View Seating Options' });

	const ctaOnClick = currentTableSupportsNewParams
		? isPaymentStepRequired
			? handleReviewClick
			: handleConfirmChangesClick
		: handleSeatingChangeClick;

	return (
		<>
			<div className="sticky top-0 z-10 bg-tablz-dark-gray-2">
				<Header
					title={t('titles.booking_details')}
					subtitle={restaurant.displayName || restaurant.name}
					iconLeft={<SquareIconButton variant='back' />}
					iconLeftOnClick={handleBackClick}
					iconRight={<SquareIconButton variant='exit' />}
					iconRightOnClick={handleExitClick}
				/>
			</div>

			{deletePendingMutationState === 'pending' || reservation.status === TDR.Reservation.Status.Pending ? (
				<Skeleton width='100%' height='100%' />
			) : (
				<section className="bg-tablz-dark-gray-1 px-4 py-5 flex flex-col gap-6 grow overflow-y-scroll" id='scrollableArea'>
					<PartySizePicker
						searchFilters={searchFilters}
						onChange={handleGroupSizeChange}
						options={groupSizeOptions}
						defaultValue={searchFilters?.groupSize || undefined}
						analyticsOption={
							selectedTable
								? { searchFlow: 'find-a-table' }
								: {
									tablesShown: results?.metadata?.totalAvailable
								}
						}
					/>
					<AvailabilityDatePicker
						disabled={searchFilters.groupSize === undefined}
						onChange={handleDateChange}
						filters={searchFilters}
						tables={tables}
						restaurant={restaurant}
					/>

					<div id={HASH_ELEMENT_IDS.timeslots}>
						{!tableIds || !selectedDate || results?.metadata?.time.length === 0 ? null : (
							<TimeSelector
								restaurant={restaurant}
								existingReservationId={reservationId}
								table={null}
								searchFilters={searchFilters}
								tableIds={tableIds}
								onTimeslotChange={(value) => {
									handleTimeslotChange(value);
								}}
							/>
						)}
					</div>
				</section>
			)}

			<footer className="bg-tablz-dark-gray-2 sticky w-full bottom-0 flex flex-col gap-3 p-2 border-t border-tablz-med-gray-4 sm:rounded-b-2xl">
				<ModificationNotice
					variant={
						areFiltersComplete && !currentTableSupportsNewParams
							? 'seatingChange'
							: areFiltersComplete && isPaymentStepRequired && !isLoading
								? 'priceChange'
								: null
					}
				/>

				<BookingDetailsSummary searchFilters={searchFilters} />

				<CTAButton
					variant='primary'
					buttonText={ctaText}
					onClick={ctaOnClick}
					disabled={
						isLoading ||
						!methods.formState.isDirty ||
						!selectedTime ||
						modifyReservationMutation.isPending ||
						deletePendingMutationState === 'pending' ||
						(currentTableSupportsNewParams && !newInvoice)
					}
					loading={isLoading || modifyReservationMutation.isPending}
				/>
			</footer>

			{/* MODALS */}

			<DiscardChanges
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onDiscard={handleConfirmExit}
				onCancel={handleCancelExit}
			/>

			<SameDayBooking open={sameDayBookingOpen} onOpenChange={setSameDayBookingOpen} restaurant={restaurant} />

			<UnavailableDate
				date={lastUnavailableDateClicked}
				groupSize={searchFilters.groupSize}
				restaurant={restaurant}
				open={noAvailabilityOpen}
				onOpenChange={setNoAvailabilityOpen}
				closedDateDescription={getClosedDateDescription(lastUnavailableDateClicked, restaurant)}
			/>

			{/* This one's not used in mod flow right now */}
			{/* <PastHorizonInfo
				entityWithPolicies={entityWithPolicies}
				isOpen={pastHorizonOpen}
				onClose={() => setPastHorizonOpen(false)}
			/> */}

			{/* <NoAvailability
				isOpen={noAvailabilityOpen}
				onClose={() => setNoAvailabilityOpen(false)}
				date={lastUnavailableDateClicked}
				groupSize={searchFilters.groupSize}
				restaurant={restaurant}
			/> */}
		</>
	);
};

export default ModifcationSearchTables;
